import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Maximize, BedDouble, Scale } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { priceRange, STATUS_LABEL } from "@/lib/constants";

export default function PropertyCard({ property, index = 0 }) {
  const { isShortlisted, toggleShortlist, isCompared, toggleCompare } = useApp();
  const p = property;
  const shortlisted = isShortlisted(p.slug);
  const compared = isCompared(p.slug);

  return (
    <article
      data-testid={`property-card-${p.slug}`}
      className="group relative flex flex-col rounded-md border border-border bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative h-56 overflow-hidden bg-muted">
        <Link to={`/properties/${p.slug}`} aria-label={p.name}>
          <img
            src={p.images?.[0]}
            alt={`${p.name}, ${p.location_name}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </Link>
        <div className="absolute top-3 left-3 flex gap-2">
          {p.featured && (
            <span className="bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm">Featured</span>
          )}
          <span className="bg-primary/90 text-primary-foreground text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm">
            {STATUS_LABEL[p.status] || p.status}
          </span>
        </div>
        <button
          data-testid={`shortlist-${p.slug}`}
          onClick={() => toggleShortlist(p)}
          aria-label={shortlisted ? "Remove from shortlist" : "Add to shortlist"}
          aria-pressed={shortlisted}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
        >
          <Heart className={`h-4 w-4 ${shortlisted ? "fill-destructive text-destructive" : "text-primary"}`} aria-hidden />
        </button>
      </div>

      <div className="flex flex-col flex-1 p-5">
        <p className="text-xs uppercase tracking-[0.14em] text-accent font-semibold mb-1.5">{p.developer_name}</p>
        <Link to={`/properties/${p.slug}`}>
          <h3 className="font-display text-lg text-foreground leading-snug hover:text-primary transition-colors">{p.name}</h3>
        </Link>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" aria-hidden /> {p.location_name}, Pune
        </p>

        <div className="mt-4 flex items-center gap-4 text-sm text-foreground/80">
          {p.bedrooms?.length > 0 && (
            <span className="flex items-center gap-1"><BedDouble className="h-4 w-4 text-primary" aria-hidden />{p.configuration?.join(", ")}</span>
          )}
          <span className="flex items-center gap-1"><Maximize className="h-4 w-4 text-primary" aria-hidden />{p.area_min}-{p.area_max} {p.area_unit}</span>
        </div>

        <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Price</p>
            <p className="font-display text-lg text-primary">{priceRange(p)}</p>
          </div>
          <button
            data-testid={`compare-${p.slug}`}
            onClick={() => toggleCompare(p)}
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-sm border transition-colors ${compared ? "bg-primary text-primary-foreground border-primary" : "border-border text-primary hover:border-primary"}`}
          >
            <Scale className="h-3.5 w-3.5" aria-hidden /> {compared ? "Added" : "Compare"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Link
            to={`/properties/${p.slug}`}
            data-testid={`view-${p.slug}`}
            className="text-center text-sm font-semibold py-2.5 rounded-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            View Details
          </Link>
          <Link
            to={`/properties/${p.slug}#enquire`}
            data-testid={`enquire-${p.slug}`}
            className="text-center text-sm font-semibold py-2.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Enquire Now
          </Link>
        </div>
      </div>
    </article>
  );
}
